import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { mockUserBalance, mockOrderBook } from '../mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, TrendingUp, TrendingDown, Clock, Zap, DollarSign, Check, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = '';

const Trade = () => {
  const { pair } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tradeType, setTradeType] = useState('binary');  // Changed default to binary
  const [orderType, setOrderType] = useState('buy');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [leverage, setLeverage] = useState('10');
  const [currentPair, setCurrentPair] = useState(null);
  const [orderBook, setOrderBook] = useState(mockOrderBook);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Binary trading states
  const [binaryDurations, setBinaryDurations] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [binaryAmount, setBinaryAmount] = useState('');
  const [activeTrades, setActiveTrades] = useState([]);
  const [completedTrades, setCompletedTrades] = useState([]);
  const [binaryStats, setBinaryStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { marketAPI, walletAPI } = await import('../services/api');
        
        // Fetch pair data
        const pairResponse = await marketAPI.getPrice(pair);
        setCurrentPair(pairResponse.data);

        // Fetch order book
        const orderBookResponse = await marketAPI.getOrderBook(pair);
        setOrderBook(orderBookResponse.data);

        // Fetch wallet balance
        const balanceResponse = await walletAPI.getBalance();
        setBalance(balanceResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({ title: 'Error', description: 'Failed to load trading data', variant: 'destructive' });
      }
    };

    fetchData();
    fetchBinaryData();
    
    // Refresh data every 10 seconds
    const interval = setInterval(() => {
      fetchData();
      fetchBinaryData();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [pair, toast]);
  
  const fetchBinaryData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      // Fetch durations
      const durationsRes = await axios.get(`${API_URL}/api/binary/durations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (durationsRes.data.success) {
        setBinaryDurations(durationsRes.data.durations);
        if (!selectedDuration && durationsRes.data.durations.length > 0) {
          setSelectedDuration(durationsRes.data.durations[2]); // Default to 2m
        }
      }
      
      // Fetch active trades
      const activeRes = await axios.get(`${API_URL}/api/binary/active-trades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (activeRes.data.success) {
        setActiveTrades(activeRes.data.trades);
      }
      
      // Fetch completed trades
      const completedRes = await axios.get(`${API_URL}/api/binary/completed-trades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (completedRes.data.success) {
        setCompletedTrades(completedRes.data.trades);
      }
      
      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/binary/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsRes.data.success) {
        setBinaryStats(statsRes.data.stats);
      }
    } catch (error) {
      console.error('Error fetching binary data:', error);
    }
  };
  
  const handleBinaryTrade = async (direction) => {
    if (!binaryAmount || parseFloat(binaryAmount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }
    
    if (!selectedDuration) {
      toast({ title: 'Error', description: 'Please select a duration', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/binary/trade`,
        {
          pair: pair,
          amount: parseFloat(binaryAmount),
          direction: direction,
          duration: selectedDuration.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({
          title: 'Trade Placed!',
          description: `${direction.toUpperCase()} trade for $${binaryAmount} placed successfully`
        });
        setBinaryAmount('');
        fetchBinaryData();
        
        // Refresh balance
        const { walletAPI } = await import('../services/api');
        const balanceResponse = await walletAPI.getBalance();
        setBalance(balanceResponse.data);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to place trade',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const getRemainingTime = (expiryTime) => {
    const now = new Date();
    const expiry = new Date(expiryTime);
    const diff = Math.max(0, expiry - now);
    
    const seconds = Math.floor((diff / 1000) % 60);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleTrade = async () => {
    if (!amount) {
      toast({ title: 'Error', description: 'Please enter amount', variant: 'destructive' });
      return;
    }

    setLoading(true);

    try {
      const { tradingAPI } = await import('../services/api');
      
      if (tradeType === 'spot') {
        const response = await tradingAPI.spotTrade({
          pair: pair,
          type: orderType,
          amount: parseFloat(amount),
          price: price ? parseFloat(price) : null
        });

        toast({
          title: 'Trade Successful!',
          description: `${orderType === 'buy' ? 'Bought' : 'Sold'} ${amount} ${pair.split('/')[0]}`,
        });

        // Refresh balance
        const { walletAPI } = await import('../services/api');
        const balanceResponse = await walletAPI.getBalance();
        setBalance(balanceResponse.data);
        
        setAmount('');
        setPrice('');
      } else if (tradeType === 'futures') {
        const response = await tradingAPI.openFutures({
          symbol: pair,
          side: orderType === 'buy' ? 'long' : 'short',
          leverage: parseInt(leverage),
          amount: parseFloat(amount),
          price: price ? parseFloat(price) : null
        });

        toast({
          title: 'Position Opened!',
          description: `${orderType === 'buy' ? 'Long' : 'Short'} ${leverage}x position opened`,
        });

        setAmount('');
        setPrice('');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Trade failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (!currentPair) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b">
        <div className="flex items-center justify-between px-4 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center">
            <div className="font-bold text-base">{currentPair.symbol}</div>
            <div className="text-xs text-gray-500">{currentPair.name}</div>
          </div>
          <button className="w-9 h-9 rounded-full bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center">
            <span className="text-sm">★</span>
          </button>
        </div>

        {/* Price Info */}
        <div className="px-4 pb-4">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-bold">${currentPair.price.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            <span className={`text-sm font-medium ${
              currentPair.change >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {currentPair.change >= 0 ? '+' : ''}{currentPair.change.toFixed(2)}%
            </span>
          </div>
          <div className="flex gap-6 text-xs text-gray-500">
            <div>
              <span className="block">24h High</span>
              <span className="text-black font-semibold">${(currentPair.price * 1.02).toFixed(2)}</span>
            </div>
            <div>
              <span className="block">24h Low</span>
              <span className="text-black font-semibold">${(currentPair.price * 0.98).toFixed(2)}</span>
            </div>
            <div>
              <span className="block">24h Vol</span>
              <span className="text-black font-semibold">1.2B</span>
            </div>
          </div>
        </div>
      </div>

      {/* Trade Type Tabs */}
      <div className="border-b">
        <div className="flex px-4">
          {['spot', 'futures', 'options'].map(type => (
            <button
              key={type}
              onClick={() => setTradeType(type)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tradeType === type
                  ? 'border-yellow-400 text-black'
                  : 'border-transparent text-gray-500'
              }`}
            >
              {type === 'spot' && 'Spot'}
              {type === 'futures' && 'Futures'}
              {type === 'options' && 'Options'}
            </button>
          ))}
        </div>
      </div>

      {/* Order Book Preview */}
      <div className="px-4 py-3 border-b">
        <div className="text-xs font-semibold mb-2 text-gray-600">Order Book</div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Bids</div>
            {mockOrderBook.bids.slice(0, 3).map((bid, idx) => (
              <div key={idx} className="flex justify-between text-xs py-0.5">
                <span className="text-green-600 font-medium">${bid.price.toLocaleString()}</span>
                <span className="text-gray-500">{bid.amount}</span>
              </div>
            ))}
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Asks</div>
            {mockOrderBook.asks.slice(0, 3).map((ask, idx) => (
              <div key={idx} className="flex justify-between text-xs py-0.5">
                <span className="text-red-600 font-medium">${ask.price.toLocaleString()}</span>
                <span className="text-gray-500">{ask.amount}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trading Panel */}
      <div className="p-4">
        {/* Buy/Sell Toggle */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={() => setOrderType('buy')}
            className={`py-3 rounded-lg font-semibold transition-colors ${
              orderType === 'buy'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Buy
          </button>
          <button
            onClick={() => setOrderType('sell')}
            className={`py-3 rounded-lg font-semibold transition-colors ${
              orderType === 'sell'
                ? 'bg-red-500 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Sell
          </button>
        </div>

        {/* Leverage (for futures) */}
        {tradeType === 'futures' && (
          <div className="mb-4">
            <label className="text-sm text-gray-600 mb-2 block">Leverage</label>
            <div className="flex gap-2">
              {['5', '10', '20', '50', '100'].map(lev => (
                <button
                  key={lev}
                  onClick={() => setLeverage(lev)}
                  className={`flex-1 py-2 rounded text-sm font-medium ${
                    leverage === lev
                      ? 'bg-yellow-400 text-black'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {lev}x
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Price Input */}
        <div className="mb-4">
          <label className="text-sm text-gray-600 mb-2 block">Price (USDT)</label>
          <Input
            type="number"
            placeholder={`Market price: ${currentPair.price}`}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="h-12"
          />
        </div>

        {/* Amount Input */}
        <div className="mb-4">
          <label className="text-sm text-gray-600 mb-2 block">Amount ({currentPair.symbol.split('/')[0]})</label>
          <Input
            type="number"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-12"
          />
        </div>

        {/* Quick Amount Buttons */}
        <div className="flex gap-2 mb-4">
          {['25%', '50%', '75%', '100%'].map(percent => (
            <button
              key={percent}
              onClick={() => {
                if (balance && balance.USDT) {
                  const availableBalance = balance.USDT;
                  const amountToSet = (availableBalance * parseFloat(percent) / 100 / currentPair.price).toFixed(4);
                  setAmount(amountToSet);
                }
              }}
              className="flex-1 py-2 rounded bg-gray-100 text-gray-600 text-sm font-medium hover:bg-gray-200"
            >
              {percent}
            </button>
          ))}
        </div>

        {/* Balance Info */}
        <div className="flex justify-between text-sm text-gray-600 mb-4">
          <span>Available:</span>
          <span className="font-semibold">
            {balance ? balance.USDT.toLocaleString() : '0'} USDT
          </span>
        </div>

        {/* Trade Button */}
        <Button
          onClick={handleTrade}
          disabled={loading}
          className={`w-full h-12 font-semibold rounded-full ${
            orderType === 'buy'
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-red-500 hover:bg-red-600 text-white'
          }`}
        >
          {loading ? 'Processing...' : `${orderType === 'buy' ? 'Buy' : 'Sell'} ${currentPair.symbol.split('/')[0]}`}
        </Button>
      </div>
    </div>
  );
};

export default Trade;